import { Formik } from 'formik';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { AddressSuggestion, useCheckoutState } from '../../../utils/checkoutState';
import { config } from '../../../utils/config';
import { getInitialDynamicFieldValues, useDynamicFields } from '../../../utils/dynamicFields';
import { reportErrorToSentry } from '../../../utils/initializeSentry';
import { getAddressForSubmission, getInitialAddressValues } from '../../../utils/address';
import Button from '../../atoms/Button';
import AddressForm from '../AddressForm';
import AddressSuggestions from '../AddressSuggestions';
import { showDefaultError } from '../Notifications';
import AddressLegend from './AddressLegend';

export type BillingAddressFormProps = {
  isEditingBillingAddress: boolean;
  setEditingBillingAddress: (isEditingBillingAddress: boolean) => void;
  isLoading?: boolean;
};

const BillingAddressForm = (props: BillingAddressFormProps) => {
  const { isEditingBillingAddress, setEditingBillingAddress, isLoading } = props;
  const { setBillingAddress, checkoutState } = useCheckoutState();
  const isCollectionPoint = !!checkoutState.orderState.addresses?.shipping?.collectionPoint;
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>();
  const { submitDynamicFieldArea } = useDynamicFields();

  const availableCountries = config.availableCountries.billing?.map(({ iso2Code }) => iso2Code) ?? [config.country];
  const initialCountry = availableCountries.find(country => country === config.country) ?? '';

  const initialValues = {
    billingAddress: {
      ...(checkoutState.orderState.addresses?.billing
        ? getInitialAddressValues(checkoutState.orderState.addresses.billing)
        : {
            country: initialCountry,
            gender: 'f',
            title: '',
            firstName: '',
            lastName: '',
            streetHouseNumber: '',
            street: '',
            houseNumber: '',
            additional: '',
            zipCode: '',
            city: '',
            state: '',
          }),
      ...getInitialDynamicFieldValues(checkoutState, 'billingAddress'),
    },
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      const _addressSuggestions = await setBillingAddress(getAddressForSubmission(values.billingAddress));
      if (_addressSuggestions) {
        setAddressSuggestions(_addressSuggestions);
        return;
      }
      await submitDynamicFieldArea(
        {
          area: 'billingAddress',
          context: 'billingAddress',
          conditionValues: { billingCountry: values.billingAddress.country },
        },
        values
      );
      setEditingBillingAddress(false);
    } catch (e) {
      console.error(e);
      showDefaultError();
      reportErrorToSentry(e);
    }
  };

  if (!isEditingBillingAddress && checkoutState.orderState.addresses?.billing) {
    return (
      <>
        <div className="option__form">
          <div className="billing-address-form">
            <Button
              className="billing-address-form__change-btn"
              type="button"
              onClick={() => setEditingBillingAddress(true)}
              disabled={isLoading}
            >
              <FormattedMessage id="generic.change" />
            </Button>
            <AddressLegend address={checkoutState.orderState.addresses.billing} />
          </div>
        </div>
      </>
    );
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ submitForm, isSubmitting, values }) => (
        <>
          <div className="option__form">
            {addressSuggestions && (
              <AddressSuggestions
                addresses={addressSuggestions}
                context="billingAddress"
                onClose={() => setAddressSuggestions(undefined)}
              />
            )}
            <AddressForm
              headline={isCollectionPoint ? <FormattedMessage id="address.billingAddress" /> : undefined}
              context="billingAddress"
              availableCountries={availableCountries}
            />
            <Button
              type="button"
              className="billing-address-form__apply-btn"
              onClick={() => submitForm()}
              disabled={isSubmitting || !values.billingAddress.country}
              isLoading={isSubmitting || isLoading}
            >
              <FormattedMessage id="generic.apply" />
            </Button>
          </div>
        </>
      )}
    </Formik>
  );
};

export default BillingAddressForm;
